import React, { useState, useEffect } from 'react';
import progressReportService from '../../services/progressReportService';
import CourseStatusChart from '../Reports/CourseStatusChart';
import CompletionRateChart from '../Reports/CompletionRateChart';

/**
 * Component for displaying a detailed progress report section on the dashboard
 */
const ProgressReportSection: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);
  const [generatedAt, setGeneratedAt] = useState<string>('');

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const data = await progressReportService.getOverallProgressReport();
        if (data && data.report) {
          setReport(data.report);
          setGeneratedAt(data.report.generatedAt || new Date().toISOString());
        }
        setError(null);
      } catch (err) {
        setError('Failed to load progress report data');
        console.error('Error fetching progress report:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  if (loading) {
    return (
      <div className="dashboard-card">
        <div className="flex justify-center items-center p-md">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-card">
        <div className="text-error p-sm">{error}</div>
      </div>
    );
  }

  if (!report || !report.summary) {
    return (
      <div className="dashboard-card">
        <div className="p-sm">No report data available.</div>
      </div>
    );
  }

  const { summary } = report;
  
  // Get CSS variables for consistent colors
  const getComputedStyle = window.getComputedStyle(document.documentElement);
  const primaryColor = getComputedStyle.getPropertyValue('--color-primary-main').trim() || '#098ac8'; // Accent color
  const primaryLightColor = getComputedStyle.getPropertyValue('--color-primary-light').trim() || '#3aa5d8';
  // Used in other parts of the component
  const successColor = getComputedStyle.getPropertyValue('--color-success').trim() || '#4caf50';
  const errorColor = getComputedStyle.getPropertyValue('--color-error').trim() || '#f44336';
  const textPrimaryColor = getComputedStyle.getPropertyValue('--color-text-primary').trim() || '#ffffff';
  
  // Extract course status data
  const completedCourses = summary.completedCourses;
  const inProgressCourses = summary.inProgressCourses;
  const notStartedCourses = summary.notStartedCourses;
  const notCompletedCourses = summary.notCompletedCourses || 0; // Add new status with fallback
  // Used in the table below
  const totalCourses = summary.totalCourses;
  
  // Used for status indicators
  const infoColor = getComputedStyle.getPropertyValue('--color-info').trim() || '#2196f3';
  const warningColor = getComputedStyle.getPropertyValue('--color-warning').trim() || '#ff9800';

  // Prepare data for the overall completion rate pie chart
  const completionRate = summary.completionRate;
  const remainingRate = 100 - completionRate;
  
  const completionRateData = [
    { name: 'Completed', value: completionRate, color: primaryColor }, // Use accent color
    { name: 'Remaining', value: remainingRate, color: textPrimaryColor }
  ];

  // Format date for display
  const formattedDate = new Date(generatedAt).toLocaleString();

  return (
    <div className="dashboard-card mt-xl">
      <h2 className="dashboard-card-title mb-xs">Overall Progress Report</h2>
      <p className="dashboard-card-label mb-md">Generated at: {formattedDate}</p>
      
      <h3 className="mb-sm">Summary</h3>
      
      <div className="flex gap-lg mb-lg" style={{ flexWrap: 'wrap' }}>
        {/* Course Status Distribution Chart */}
        <div className="chart-container" style={{ flex: '1 1 45%', minWidth: '300px' }}>
          <h4 className="mb-sm">Course Status Distribution</h4>
          <div style={{ height: '250px' }}>
            <CourseStatusChart
              completedCourses={completedCourses}
              inProgressCourses={inProgressCourses}
              notStartedCourses={notStartedCourses}
              notCompletedCourses={notCompletedCourses}
            />
          </div>
        </div>
        
        {/* Overall Completion Rate Chart */}
        <div className="chart-container" style={{ flex: '1 1 45%', minWidth: '300px' }}>
          <h4 className="mb-sm">Overall Completion Rate</h4>
          <div style={{ height: '250px' }}>
            <CompletionRateChart completionRate={completionRate} />
          </div>
        </div>
      </div>
      
      {/* Summary Table */}
      <div className="mb-lg" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Total Customers</th>
              <th>Total Users</th>
              <th>Total Courses</th>
              <th>Completed Courses</th>
              <th>In Progress Courses</th>
              <th>Not Started Courses</th>
              <th>Not Completed Courses</th>
              <th>Completion Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{summary.totalCustomers}</td>
              <td>{summary.totalUsers}</td>
              <td>{summary.totalCourses}</td>
              <td>{summary.completedCourses}</td>
              <td>{summary.inProgressCourses}</td>
              <td>{summary.notStartedCourses}</td>
              <td>{summary.notCompletedCourses || 0}</td>
              <td>{summary.completionRate.toString().replace(',', '.')}%</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div className="text-right">
        <a href="/reports" className="dashboard-card-link">
          View full report
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </a>
      </div>
    </div>
  );
};

export default ProgressReportSection;
